import * as React from 'react';
import { Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';
import { Dashboard } from '@app/Dashboard/Dashboard';
import { SecretManager } from '@app/SecretManager/SecretsExplorer/view/index';
import { MicroserviceGenerator } from '@app/MicroserviceGenerator/view/index';
import { NotFound } from '@app/NotFound/NotFound';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { usePageView } from '@app/hooks/usePageView';
import { ChangeRequestIndex } from '@app/SecretManager/ChangeRequests/view';
import { CreateEnvWizard } from '@app/GranularDeploy/components/CreateEnvWizard';
import { PreviewEnvWizard } from '@app/PreviewEnv/components/PreviewEnvWizard';
import { MCPCatalog } from '@app/MCPCatalog/MCPCatalog';
import { QuickLinks } from '@app/QuickLinks/QuickLinks';
import { Analytics } from '@app/Analytics/Analytics';
import { UsageAnalyticsPage } from '@app/UsageAnalytics';
import { AUDIT, ENABLE_ALERTS, GRANULAR } from './config';
import { MergeQueue } from './MergeQueue';
import { DeployTelapp } from './DeployTelapp';
import { AlertsAdminPage } from './Admin/Alerts/AlertsAdminPage';
import { AppConfigsPage } from './Admin/AppConfigs/AppConfigsPage';
import { EodBudgetAdminPage } from './Admin/EodBudget/EodBudgetAdminPage';
import { CatalogPage } from './AIChatbots/CatalogPage/CatalogPage';
import { ChatPage } from './AIChatbots/ChatPage/ChatPage';
import { useEntraAppRoles } from './hooks/useEntraRoles';
import { RiskRegister } from './RiskRegister';

let routeFocusTimer: number;

export type NavSection = 'generators' | 'ai' | 'build' | 'deploy' | 'secretmanager' | 'resources' | 'analytics' | 'monitoring' | 'admin';

export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  /* eslint-disable @typescript-eslint/no-explicit-any */
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  exact?: boolean;
  path: string;
  title: string;
  routes?: undefined;
  visible?: boolean;
  navSection?: NavSection;
  navIcon?: string;
  /** When set, the route is only registered for users with this Entra app role (or any of the listed roles). */
  requireRole?: string | string[];
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
  visible?: boolean;
  showInMainNav?: boolean;
  navSection?: NavSection;
  navIcon?: string;
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

/** Same check for `<Switch>` registration and sidebar: gated routes only when the user has `requireRole`. */
export function isAppRouteForUser(route: IAppRoute, userRoles: readonly string[]): boolean {
  if (!route.requireRole) return true;
  const required = Array.isArray(route.requireRole) ? route.requireRole : [route.requireRole];
  return required.some((role) => userRoles.includes(role));
}

const routes: AppRouteConfig[] = [
  // BUILD
  ...(
    GRANULAR
      ? [
        {
          label: 'Microservice Generator',
          component: MicroserviceGenerator,
          exact: false,
          path: '/microservicegenerator',
          title: 'Microservice Generator',
          navSection: 'generators' as NavSection,
          navIcon: '⚡',
        }
      ]
      : []
  ),
  // AI HUB
  {
    label: 'AI Agents',
    component: CatalogPage,
    exact: true,
    path: '/ai-chat-agents',
    title: 'AI Agents',
    navSection: 'ai' as NavSection,
    navIcon: '__ai-agents__',
  },
  {
    label: 'AI Chat',
    component: ChatPage,
    exact: false,
    path: '/ai-chat-agents/chat',
    title: 'AI Chat',
    navSection: 'ai' as NavSection,
    navIcon: '💬',
  },
  {
    label: 'MCP Catalog',
    component: MCPCatalog,
    exact: true,
    path: '/mcp-catalog',
    title: 'MCP Catalog',
    navSection: 'ai' as NavSection,
    navIcon: '🗂️',
  },
  // DEPLOY
  {
    label: 'Secrets Explorer',
    component: SecretManager,
    exact: false,
    path: '/secretmanager/secretsexplorer',
    title: 'Secrets Explorer',
    navSection: 'deploy' as NavSection,
    navIcon: '🔑',
  },
  {
    label: 'Change Requests',
    component: ChangeRequestIndex,
    exact: false,
    path: '/secretmanager/changerequests',
    title: 'Change Requests',
    navSection: 'deploy' as NavSection,
    navIcon: '📝',
  },
  {
    component: DeployTelapp,
    exact: true,
    label: 'Deployments - Telapp',
    path: '/deploy/telapp',
    title: 'Deployments - Telapp',
    visible: true,
    navSection: 'deploy' as NavSection,
    navIcon: '🏷️',
  },
  ...(
    GRANULAR
      ? [
        {
          component: CreateEnvWizard,
          exact: false,
          label: 'Manage Deployment',
          path: '/granular-deploy',
          title: 'Manage Deployment',
          visible: true,
          navSection: 'deploy' as NavSection,
          navIcon: '📦',
        },
        {
          component: PreviewEnvWizard,
          exact: false,
          label: 'Preview Environment',
          path: '/preview-env',
          title: 'Preview Environment',
          visible: true,
          navSection: 'deploy' as NavSection,
          navIcon: '🔭',
          requireRole: ['BetaTest', 'Admin'],
        },
      ]
      : []
  ),
  {
    component: Dashboard,
    exact: true,
    label: 'Environment Dashboard',
    path: '/',
    title: 'Environment Dashboard',
    navSection: 'deploy' as NavSection,
    navIcon: '🌍',
  },
  // ADMIN
  ...(AUDIT
    ? [
      {
        label: 'Event Tracking',
        component: Analytics,
        exact: true,
        path: '/admin/event-tracking',
        title: 'Admin — Event Tracking',
        navSection: 'admin' as NavSection,
        navIcon: '📍',
        requireRole: 'Admin',
      },
      {
        label: 'Usage Analytics',
        component: UsageAnalyticsPage,
        exact: true,
        path: '/admin/usage-analytics',
        title: 'Admin — Usage Analytics',
        navSection: 'admin' as NavSection,
        navIcon: '📈',
        requireRole: 'Admin',
      },
    ]
    : []),
  // MONITORING
  {
    label: 'Risk Register',
    component: RiskRegister,
    exact: true,
    path: '/risk-register',
    title: 'Risk Register',
    navSection: 'monitoring' as NavSection,
    navIcon: '⚠️',
  },
  // RESOURCES
  {
    label: 'Quick Links',
    component: QuickLinks,
    exact: true,
    path: '/quick-links',
    title: 'Quick Links',
    navSection: 'resources' as NavSection,
    navIcon: '🚀',
  },
  // BUILD
  {
    label: 'Merge Queue - Telapp',
    component: MergeQueue,
    exact: true,
    path: '/merge-queue/telapp',
    title: 'Merge Queue - Telapp',
    navSection: 'build' as NavSection,
    navIcon: '🔀',
  },
  ...(ENABLE_ALERTS ? [{
    label: 'Alerts',
    component: AlertsAdminPage,
    exact: true,
    path: '/admin/alerts',
    title: 'Admin — Alerts',
    navSection: 'admin' as NavSection,
    navIcon: '🔔',
    requireRole: 'Admin',
  }] : []),
  {
    label: 'App Configs',
    component: AppConfigsPage,
    exact: true,
    path: '/admin/app-configs',
    title: 'Admin — App Configs',
    navSection: 'admin' as NavSection,
    navIcon: '🎛️',
    requireRole: 'Admin',
  },
  {
    label: 'EOD Budget',
    component: EodBudgetAdminPage,
    exact: true,
    path: '/admin/eod-budget',
    title: 'Admin — EOD Budget',
    navSection: 'admin' as NavSection,
    navIcon: '💰',
    requireRole: 'Admin',
  },
];

// a custom hook for sending focus to the primary content container
// after a view has loaded so that subsequent press of tab key
// sends focus directly to relevant content
// may not be necessary if https://github.com/ReactTraining/react-router/issues/5210 is resolved
const useA11yRouteChange = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    routeFocusTimer = window.setTimeout(() => {
      const mainContainer = document.getElementById('primary-app-container');
      if (mainContainer) {
        mainContainer.focus();
      }
    }, 50);
    return () => {
      window.clearTimeout(routeFocusTimer);
    };
  }, [pathname]);
};

function titleToEventName(title: string): string {
  return 'PAGE_VISITED_' + title.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
}

const RouteWithTitleUpdates = ({ component: Component, title, ...rest }: IAppRoute) => {
  useA11yRouteChange();
  useDocumentTitle(title);
  usePageView(titleToEventName(title));

  function routeWithTitle(routeProps: RouteComponentProps) {
    return <Component {...rest} {...routeProps} />;
  }

  return <Route render={routeWithTitle} {...rest} />;
};

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title);
  return <Route component={NotFound} />;
};

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
  [] as IAppRoute[]
);

const AppRoutes = (): React.ReactElement => {
  const userRoles = useEntraAppRoles();

  const routesForUser = React.useMemo(
    () => flattenedRoutes.filter((r) => isAppRouteForUser(r, userRoles)),
    [userRoles]
  );

  return (
    <Switch>
      {routesForUser.map(({ path, exact, component, title }) => (
        <RouteWithTitleUpdates path={path} exact={exact} component={component} key={path} title={title} />
      ))}
      <PageNotFound title="404 Page Not Found" />
    </Switch>
  );
};

export { AppRoutes, routes };
